export const mockProjects = [
  {
    id: 1,
    title: "VeloDesk.com - AI-Powered ITSM for Healthcare",
    category: "Velo Platform",
    year: "2026",
    status: "Completed",
    company: "Velozent Technologies",
    role: "Head of AI | AI Engineering Manager",
    description: "Agentic IT service management for healthcare enterprises, with a multi-agent triage pipeline reaching 94% routing accuracy.",
    longDescription: "VeloDesk is the ITSM product in the Velozent Agentic Enterprise Suite. A multi-agent triage pipeline (CrewAI classifier plus LangGraph router) on a Qwen 2.5-7B backbone coordinates dedicated agents for ticket classification, SLA monitoring, knowledge retrieval, escalation routing and client communication. A Tier-1 knowledge assistant retrieves step-by-step resolution guidance via RAG over internal runbooks and historical tickets, predictive SLA-breach alerting runs on time-series anomaly detection, and agent memory persists ticket context across sessions. REST integrations cover Jira, Slack, Microsoft Teams and PagerDuty.",
    technologies: ["LangGraph", "CrewAI", "Qwen 2.5-7B", "GPT-4o", "FastAPI", "WebSockets", "Weaviate", "PostgreSQL", "Azure"],
    impact: ["94% ticket routing accuracy", "52% reduction in first-response time", "60% of routine client communications self-served"],
    image: "/projects/velodesk.jpg",
    featured: true
  },
  {
    id: 2,
    title: "VeloContract.com - AI-Powered Contract Lifecycle Management",
    category: "Velo Platform",
    year: "2026",
    status: "Completed",
    company: "Velozent Technologies",
    role: "Head of AI | AI Engineering Manager",
    description: "Agentic CLM built on a four-agent LangGraph pipeline with human-in-the-loop checkpoints, cutting legal review time by 70%.",
    longDescription: "VeloContract runs a four-agent pipeline in LangGraph with human-in-the-loop checkpoints: Extraction, Risk Scoring, Compliance and Obligation Tracking. The Extraction Agent uses a local Mistral-7B parser over LlamaIndex and Weaviate RAG to pull clauses, obligations, dates, counterparties and risk indicators from unstructured PDFs without sending data to external APIs. The Compliance Agent benchmarks every clause against HIPAA, ISO 27001 and GDPR and flags non-compliant language with remediation recommendations, while executive dashboards surface real-time risk scoring, supplier concentration, obligation burn-down and audit-ready clause change history.",
    technologies: ["LangGraph", "LlamaIndex", "Weaviate", "Mistral-7B", "GPT-4o", "FastAPI", "PostgreSQL"],
    impact: ["70% reduction in legal review time", "65% reduction in compliance incidents", "On-premise extraction with no external API calls"],
    image: "/projects/velocontract.jpg",
    featured: true
  },
  {
    id: 3,
    title: "VeloHr.com - AI-Powered HRMS",
    category: "Velo Platform",
    year: "2026",
    status: "Completed",
    company: "Velozent Technologies",
    role: "Head of AI | AI Engineering Manager",
    description: "Agentic HRMS spanning recruitment intelligence, onboarding orchestration, performance management and payroll compliance.",
    longDescription: "VeloHr covers recruitment intelligence, onboarding orchestration, performance management, payroll compliance and employee lifecycle automation. A fine-tuned recruitment agent matches candidate profiles to job descriptions with contextual scoring, an onboarding orchestration agent autonomously sequences task assignment, document collection, system provisioning and training scheduling across departments, and a RAG-powered policy assistant lets employees query policies and leave entitlements in natural language. Performance agents aggregate 360-degree feedback, generate structured review summaries and flag flight-risk signals.",
    technologies: ["LangGraph", "CrewAI", "Fine-tuned LLaMA-3", "LlamaIndex", "FAISS", "RAG", "FastAPI", "PostgreSQL"],
    impact: ["100% agreement with the human shortlisting panel", "Autonomous cross-department onboarding", "Natural-language HR policy self-service"],
    image: "/projects/velohr.jpg",
    featured: true
  },
  {
    id: 4,
    title: "VeloInventory.com - AI-Powered Asset Management",
    category: "Velo Platform",
    year: "2026",
    status: "Active",
    company: "Velozent Technologies",
    role: "Head of AI | AI Engineering Manager",
    description: "Agentic asset lifecycle intelligence for hospital equipment, from provisioning through predictive maintenance to end-of-life.",
    longDescription: "VeloInventory is the agentic asset lifecycle platform for hospital equipment, covering provisioning, maintenance scheduling, utilisation tracking and end-of-life management. An Asset Intelligence Agent monitors device health signals, maintenance logs and utilisation telemetry while anomaly detection agents flag degrading assets before failure. A predictive maintenance engine combining time-series ML with transformer sequence modelling forecasts service requirements and auto-generates work orders, and compliance agents keep certifications, calibration records and regulatory documentation audit-ready. The integration layer connects hospital CMDB, procurement systems and vendor portals.",
    technologies: ["LangGraph", "OpenAI Agents SDK", "Python", "FastAPI", "PostgreSQL", "Azure", "Time-series ML"],
    impact: ["Predictive maintenance with auto-generated work orders", "Failure prediction from device health telemetry", "Audit-ready certification and calibration records"],
    image: "/projects/veloinventory.jpg",
    featured: true
  },
  {
    id: 5,
    title: "VeloCapture - AI Lead Generation & Bid Intelligence",
    category: "Velo Platform",
    year: "2026",
    status: "Completed",
    company: "Velozent Technologies",
    role: "Product Owner",
    description: "Finds, scores and drafts responses to outbound leads, government bids and corporate RFPs, ending in a booked demo or a submitted bid.",
    longDescription: "VeloCapture finds every relevant outbound lead, government bid and corporate RFP for the products a tenant sells, scores each one, drafts the response in the buyer's own format and reaches buyers by email and voice. A Next.js front end sits over a FastAPI and Celery pipeline, with DuckDB for large-scale provider ETL, Splink for entity resolution and Playwright for procurement-portal automation.",
    technologies: ["Next.js", "FastAPI", "Celery", "PostgreSQL", "pgvector", "Redis", "DuckDB", "Splink", "Playwright"],
    impact: ["Leads, government bids and corporate RFPs in one pipeline", "Entity resolution across large provider datasets", "Automated draft responses in the buyer's own format"],
    image: "/projects/velocapture.jpg",
    featured: false
  },
  {
    id: 6,
    title: "VeloRevenue - Healthcare ERP",
    category: "Velo Platform",
    year: "2026",
    status: "Active",
    company: "Velozent Technologies",
    role: "Product Owner",
    description: "One platform from the claim to the ledger: finance, revenue cycle, supply chain, inventory, HR and payroll for hospitals.",
    longDescription: "VeloRevenue closes the seam between finance platforms that stop at the business office and revenue-cycle vendors that never touch the ledger. An 835 remittance posts cash, contractual allowance and denial reserve against patient A/R in the same transaction as the claim state update. Built for 25 to 300 bed hospitals and clinic groups on Next.js 16 and PostgreSQL 17, with an AI assistant across finance and revenue cycle.",
    technologies: ["Next.js 16", "React 19", "TypeScript", "PostgreSQL 17", "pgvector", "Valkey", "Zod", "Docker"],
    impact: ["Claim-to-ledger posting in a single transaction", "Finance, RCM, supply chain and payroll unified", "Built for 25-300 bed hospitals and clinic groups"],
    image: "/projects/velorevenue.jpg",
    featured: false
  },
  {
    id: 7,
    title: "VeloCares - Healthcare CRM",
    category: "Velo Platform",
    year: "2026",
    status: "Active",
    company: "Velozent Technologies",
    role: "Product Owner",
    description: "AI-native patient relationship and engagement platform where every touchpoint is connected and consent-safe.",
    longDescription: "VeloCares is the healthcare CRM in the Velo family: a patient relationship and engagement platform built so that every patient is known, every touchpoint connected, and every action consent-safe and provable. Next.js 16 serves both the interface and the API surface over PostgreSQL 16, with subdomain multi-tenancy and an AI gateway that degrades gracefully when no model key is configured.",
    technologies: ["Next.js 16", "TypeScript", "PostgreSQL", "jose JWT", "Anthropic Claude", "Tailwind"],
    impact: ["Consent-safe, provable patient engagement", "Subdomain multi-tenancy per health system", "Graceful AI fallbacks when no model key is set"],
    image: "/projects/velocares.jpg",
    featured: false
  },
  {
    id: 8,
    title: "VeloComms - HIPAA-Compliant Team Communication",
    category: "Velo Platform",
    year: "2026",
    status: "Active",
    company: "Velozent Technologies",
    role: "Product Owner",
    description: "Secure Teams and Slack style messaging built for healthcare, with channels, RBAC, audit and SSO.",
    longDescription: "VeloComms is the HIPAA-compliant communication platform in the Velo suite, delivering secure messaging, channels, role-based access control, audit trails and single sign-on for clinical teams. A Next.js front end sits over a FastAPI backend, containerised with continuous deployment to its own infrastructure.",
    technologies: ["Next.js", "TypeScript", "FastAPI", "Python 3.12", "Pydantic v2", "Docker", "Tailwind"],
    impact: ["HIPAA-aligned secure clinical messaging", "Channel-level RBAC with full audit trail", "Starts cleanly with optional integrations unset"],
    image: "/projects/velocomms.jpg",
    featured: false
  },
  {
    id: 9,
    title: "VeloPolicy - Policy & Procedure Management",
    category: "Velo Platform",
    year: "2026",
    status: "Active",
    company: "Velozent Technologies",
    role: "Product Owner",
    description: "Every policy current, findable and provable: for the nurse at 3 a.m., the policy office and the surveyor at the door.",
    longDescription: "VeloPolicy is the healthcare policy and procedure management module on the Velo platform, built to a 122-feature specification with a feature registry as the traceability spine. PostgreSQL with pgvector, pg_trgm and ltree powers semantic and hierarchical policy search, MinIO stores the documents, and Claude supports assisted authoring and review.",
    technologies: ["Next.js 16", "React 19", "TypeScript", "PostgreSQL", "pgvector", "ltree", "Redis", "MinIO", "Claude"],
    impact: ["122 tracked features with commit-level traceability", "Semantic and hierarchical policy search", "Survey-ready proof that policies are current"],
    image: "https://images.unsplash.com/photo-1582750433449-648ed127bb54?w=500&h=300&fit=crop&auto=format",
    featured: false
  },
  {
    id: 10,
    title: "VeloLibrary - Medical Learning Management System (LMS)",
    category: "Velo Platform",
    year: "2026",
    status: "Active",
    company: "Velozent Technologies",
    role: "Product Owner",
    description: "One learning record, from first lecture to last CME credit.",
    longDescription: "VeloLibrary is the AI-native, institution-grade medical learning platform that carries a single learner record from medical school through residency into lifetime CME. Built on Next.js 16 with Drizzle ORM over PostgreSQL 16 and row-level security, BullMQ job queues, and a weekly item-response-theory calibration batch for psychometrically sound assessment.",
    technologies: ["Next.js 16", "TypeScript", "PostgreSQL", "pgvector", "Drizzle ORM", "BullMQ", "Redis", "Claude"],
    impact: ["One learner record across med school, residency and CME", "Row-level security per institution", "IRT-calibrated assessment"],
    image: "/projects/velolibrary.jpg",
    featured: false
  },
  {
    id: 11,
    title: "VeloFacility - Facility Management",
    category: "Velo Platform",
    year: "2026",
    status: "Active",
    company: "Velozent Technologies",
    role: "Product Owner",
    description: "Facility, work order and preventive maintenance management across a health system's buildings and plant.",
    longDescription: "VeloFacility is the facility management product in the Velo family, covering work orders, preventive maintenance schedules and space and plant management across a health system's estate. Built on Next.js with Supabase-backed services and a background worker, it shares the Velo design token contract and enforces tenant-bound sessions in middleware.",
    technologies: ["Next.js", "TypeScript", "Supabase", "Tailwind", "Background Workers"],
    impact: ["Work orders and preventive maintenance in one system", "Tenant-bound sessions enforced in middleware", "Shares the Velo design token contract"],
    image: "/projects/velofacility.jpg",
    featured: false
  },
  {
    id: 12,
    title: "Night Hawk – Agentic Data Intelligence Platform",
    category: "AI Engineering",
    year: "2025",
    status: "Completed",
    company: "Culinda Inc.",
    role: "AI Engineering Manager",
    description: "Designed and deployed a fully agentic AI platform for secure data querying and visualization using OpenAI Agents SDK with GPT-4o.",
    longDescription: "Led the development of 'Night Hawk', a revolutionary agentic AI platform that orchestrates sub-agents (MongoDB, SQL, Visualization) with embedded schema-compliant guardrails for safe execution. Built on FastAPI and WebSockets to support streaming queries with incremental responses, enabling real-time Plotly-based insights for hospital threat analysis.",
    technologies: ["GPT-4o", "OpenAI Agents SDK", "FastAPI", "WebSockets", "MongoDB", "Plotly", "Langfuse"],
    impact: ["Real-time threat analysis", "Schema-compliant guardrails", "Streaming query responses"],
    image: "https://customer-assets.emergentagent.com/job_187402a5-927c-41c5-9be1-d079b56e1d1d/artifacts/xv3yik0i_darkeye_mascot.png",
    featured: false
  },
  {
    id: 13,
    title: "AI Security Copilot",
    category: "Cybersecurity",
    year: "2024",
    status: "Completed",
    company: "Culinda Inc.",
    role: "AI Engineering Manager", 
    description: "Developed a security-focused chatbot assistant powered by fine-tuned LLaMA-2 models, reducing MTTR by 40%.",
    longDescription: "Built a comprehensive security copilot that integrates with SIEM logs, ticketing systems, and vulnerability databases to support SOC analysts with real-time recommendations. Used RAG pipeline with FAISS and LangChain to combine knowledge from compliance documents and threat reports.",
    technologies: ["LLaMA-2", "FAISS", "LangChain", "SIEM", "RAG", "PyTorch"],
    impact: ["40% reduction in MTTR", "Automated triage", "92% F1 score accuracy"],
    image: "https://images.unsplash.com/photo-1563986768609-322da13575f3?w=500&h=300&fit=crop&auto=format",
    featured: false
  },
  {
    id: 14,
    title: "Dark Watch – AI Remediation Assistant",
    category: "AI Engineering",
    year: "2024", 
    status: "Completed",
    company: "Culinda Inc.",
    role: "AI Engineering Manager",
    description: "LLM-based chatbot delivering automated, RAG-enriched incident remediation with contextual retrieval capabilities.",
    longDescription: "Architected 'Dark Watch', an advanced LLM-based assistant powered by GPT-4o and integrated with LlamaIndex vector store. Capable of contextual retrieval, web lookups, and conversational delivery of step-by-step fix guides, with REST API and CLI integration into SOC workflows.",
    technologies: ["GPT-4o", "LlamaIndex", "Vector Search", "REST API", "Slack", "Jira"],
    impact: ["Automated incident remediation", "Contextual threat analysis", "SOC workflow integration"],
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=500&h=300&fit=crop&auto=format",
    featured: false
  },
  {
    id: 15,
    title: "ThreatPrism.com – Threat Intelligence Hub",
    category: "Cybersecurity",
    year: "2024",
    status: "Completed",
    company: "Primera Medical Technologies", 
    role: "AI Engineering Manager",
    description: "LLM/RAG-based threat intelligence platform with 15+ integrated tools for dynamic threat analysis.",
    longDescription: "Developed and scaled ThreatPrism, a function-calling-first LLM assistant backed by Weaviate vector search. Enables dynamic threat analysis through CVE lookups, VirusTotal, MITRE, WHOIS integration with FastAPI orchestration and Langfuse observability.",
    technologies: ["Weaviate", "FastAPI", "Langfuse", "CVE API", "VirusTotal", "MITRE", "LangChain"],
    impact: ["Daily use by 12-member team", "15+ integrated tools", "High uptime with fallback mechanisms"],
    image: "https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=500&h=300&fit=crop&auto=format",
    featured: false
  },
  {
    id: 16,
    title: "LLM Fine-Tuning & Synthetic Data Generation",
    category: "Machine Learning",
    year: "2024",
    status: "Completed",
    company: "Culinda Inc.",
    role: "AI Engineering Manager",
    description: "Advanced LLM fine-tuning using Q-LoRA on LLaMA 2 and Falcon models with synthetic data generation.",
    longDescription: "Oversaw comprehensive LLM fine-tuning experiments using Q-LoRA on models up to 50B parameters. Created 10K+ NL to MongoDB/SQL query pairs via synthetic data generation using GPT-4, managing schema-driven preprocessing and training pipelines on Databricks GPU clusters.",
    technologies: ["Q-LoRA", "LLaMA 2", "Falcon", "GPT-4", "Databricks", "MongoDB", "SQL"],
    impact: ["12% F1 score improvement", "10K+ synthetic query pairs", "Optimized LoRA ranks"],
    image: "https://images.unsplash.com/photo-1507146426996-ef05306b995a?w=500&h=300&fit=crop&auto=format",
    featured: false
  },
  {
    id: 17,
    title: "Cybersecurity Risk Quantification",
    category: "Risk Analysis", 
    year: "2023",
    status: "Completed",
    company: "Culinda Inc.",
    role: "AI Engineering Manager",
    description: "Quantified cybersecurity risk in monetary terms using probabilistic modeling and Monte Carlo simulations.",
    longDescription: "Led a comprehensive project to quantify risk for hospital chains using Probabilistic Determination, FAIR STRIDE, Monte Carlo Simulations, and statistical modeling. Developed real-time dashboard accounting for CVEs and internal threat factors.",
    technologies: ["Monte Carlo", "FAIR STRIDE", "Statistical Modeling", "CVE Analysis", "Real-time Dashboards"],
    impact: ["Executive decision support", "Data-driven security investments", "Risk monetization"],
    image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=500&h=300&fit=crop&auto=format",
    featured: false
  },
  {
    id: 18,
    title: "Dynamic Querying & Visualization System",
    category: "NLP",
    year: "2022-2023",
    status: "Completed", 
    company: "Culinda Inc.",
    role: "AI Lead",
    description: "LLM-powered system achieving 100% accuracy in natural language database queries across multiple database types.",
    longDescription: "Developed an innovative LLM-powered dynamic querying system that achieved 100% accuracy in query generation across Cassandra, MongoDB, and MySQL databases. Eliminated need for canned queries by enabling natural language interaction with auto-generated Plotly visualizations.",
    technologies: ["LLM", "Cassandra", "MongoDB", "MySQL", "Plotly", "Natural Language Processing"],
    impact: ["100% query accuracy", "Eliminated canned queries", "Auto-generated visualizations"],
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=500&h=300&fit=crop&auto=format",
    featured: false
  },
  {
    id: 19,
    title: "Auto Recruitment System",
    category: "NLP",
    year: "2022-2023",
    status: "Completed",
    company: "Recroid.com",
    role: "Co-Founder & Data Scientist",
    description: "Automated recruitment platform using fine-tuned LLMs for candidate-job matching with 100% accuracy.",
    longDescription: "Created an automated recruitment system leveraging fine-tuned LLMs to match candidate profiles with job descriptions. Achieved 100% shortlisting accuracy and significantly reduced manual screening time for HR teams through advanced natural language processing.",
    technologies: ["Fine-tuned LLMs", "NLP", "Candidate Matching", "HR Automation"],
    impact: ["100% shortlisting accuracy", "Reduced manual screening", "HR workflow automation"],
    image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=500&h=300&fit=crop&auto=format",
    featured: false
  },
  {
    id: 20,
    title: "Healthcare Threat Detection System",
    category: "Cybersecurity",
    year: "2021-2022",
    status: "Completed",
    company: "Culinda Inc.",
    role: "Principal Data Scientist",
    description: "Real-time threat detection using deep learning on IoT and network data from hospital systems.",
    longDescription: "Built a comprehensive real-time threat detection system using advanced deep learning techniques on IoT and network data from hospital systems. Implemented robust time-series anomaly detection model enabling proactive incident response and mitigation for healthcare PII and medical device security.",
    technologies: ["Deep Learning", "IoT Security", "Time-series Analysis", "Anomaly Detection", "Hospital Systems", "PII Protection"],
    impact: ["Real-time threat detection", "Proactive incident response", "Healthcare security", "IoT/IomT monitoring"],
    image: "https://images.unsplash.com/photo-1711980815489-5fdd9e24adb0?w=500&h=300&fit=crop&auto=format",
    featured: false
  },
  {
    id: 21,
    title: "ChestAI - Chest Disease Detection",
    category: "Healthcare AI",
    year: "2021-2022", 
    status: "Completed",
    company: "Yale University/ChestAI.com",
    role: "AI Advisor",
    description: "Computer vision model for chest disease detection through X-rays in partnership with Yale University.",
    longDescription: "Partnered with Yale University to build an advanced computer vision model for detecting chest diseases through X-ray analysis. Enhanced model accuracy with sophisticated image augmentation techniques and led hospital onboarding for beta validation.",
    technologies: ["Computer Vision", "Medical Imaging", "X-ray Analysis", "Image Augmentation", "Clinical AI"],
    impact: ["Medical-grade accuracy", "Hospital partnerships", "Clinical validation"],
    image: "https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=500&h=300&fit=crop&auto=format",
    featured: false
  },
  {
    id: 22,
    title: "DermaAI - Skin Disease Detection",
    category: "Healthcare AI",
    year: "2021-2022",
    status: "Completed", 
    company: "DermaAI",
    role: "Co-Founder & Data Scientist",
    description: "Computer vision platform for malignant and cosmetic skin condition identification surpassing dermatologist accuracy.",
    longDescription: "Developed a comprehensive computer vision-based diagnostic platform for identifying malignant and cosmetic skin conditions. Led end-to-end product cycle from data collection and training to commercialization, achieving accuracy levels surpassing dermatologist performance.",
    technologies: ["Computer Vision", "Medical AI", "Skin Analysis", "Diagnostic Platform", "Deep Learning"],
    impact: ["Surpassed dermatologist accuracy", "Commercial deployment", "End-to-end product development"],
    image: "https://images.unsplash.com/photo-1582750433449-648ed127bb54?w=500&h=300&fit=crop&auto=format",
    featured: false
  },
  {
    id: 23,
    title: "NeedyData - Data Cleaning Platform",
    category: "Data Engineering",
    year: "2018-2021",
    status: "Acquired",
    company: "NeedyData", 
    role: "Founder & Data Scientist",
    description: "No-code web-based platform for automated large-scale data cleaning, acquired by Canadian firm in 2021.",
    longDescription: "Launched a revolutionary no-code web-based product that automated large-scale data cleaning processes. Featured intuitive UI and ML-powered cleaning modules, successfully democratizing data cleaning for non-technical users before being acquired by a Canadian firm.",
    technologies: ["No-code Platform", "ML Automation", "Data Cleaning", "Web Platform", "User Interface"],
    impact: ["Acquired by Canadian firm", "Democratized data cleaning", "No-code innovation"],
    image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=500&h=300&fit=crop&auto=format",
    featured: false
  },
  {
    id: 24,
    title: "Flight Delay Optimization",
    category: "Operations Research",
    year: "2019-2020",
    status: "Completed",
    company: "Delta Airlines",
    role: "Data Scientist", 
    description: "Optimized national flight schedules using predictive models to reduce cascading delays and improve efficiency.",
    longDescription: "Optimized national flight schedules for Delta Airlines using sophisticated predictive models to reduce cascading delays. Improved passenger experience and airline operational efficiency through advanced data-driven planning and scheduling algorithms.",
    technologies: ["Predictive Modeling", "Operations Research", "Schedule Optimization", "Time Series", "Big Data"],
    impact: ["Reduced cascading delays", "Improved passenger experience", "Operational efficiency"],
    image: "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=500&h=300&fit=crop&auto=format",
    featured: false
  },
  {
    id: 25,
    title: "Telecommunication Logs Classification",
    category: "Machine Learning",
    year: "2017",
    status: "Completed",
    company: "Intel Deutschland/LNT Technology Services",
    role: "Senior Machine Learning Engineer",
    description: "Automated telecommunication log classification achieving 97% accuracy and saving 3,200 hours monthly.",
    longDescription: "Played key role in classifying telecommunication logs involving data ingestion, wrangling, and database schema design. Developed highly accurate model achieving 97% classification accuracy, successfully automating log routing and saving significant manual effort.",
    technologies: ["Machine Learning", "Data Engineering", "Classification", "Database Design", "Log Processing"],
    impact: ["97% classification accuracy", "3,200 hours saved monthly", "Process automation"],
    image: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=500&h=300&fit=crop&auto=format",
    featured: false
  },
  {
    id: 26,
    title: "MagixDB - Natural Language Database Querying",
    category: "NLP",
    year: "2020-2024",
    status: "Completed",
    company: "MagixDB",
    role: "Co-Founder",
    description: "Revolutionary platform enabling natural language database queries across 20+ database types with 100% accuracy.",
    longDescription: "Co-founded and developed MagixDB, a groundbreaking platform that enables users to query databases using natural language. Supports 20+ database types with 100% query accuracy, eliminating the need for technical SQL knowledge and democratizing data access.",
    technologies: ["Natural Language Processing", "Multi-database Support", "Query Generation", "Web Platform"],
    impact: ["20+ database support", "100% query accuracy", "Democratized data access"],
    image: "https://customer-assets.emergentagent.com/job_187402a5-927c-41c5-9be1-d079b56e1d1d/artifacts/cfbj7fuy_image.png",
    featured: false
  }
];
